import React, { useState } from "react";
import { Card, Tag, Typography, Row, Col, Alert, Tooltip, Modal } from "antd";
import {
    KeyOutlined,
    EnvironmentOutlined,
    CalendarOutlined,
    SafetyOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    StopOutlined,
    LoginOutlined,
    IdcardOutlined,
    AppstoreOutlined,
    BarcodeOutlined,
    ClockCircleOutlined,
    TeamOutlined,
    PictureOutlined,
    UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useQuery } from "@apollo/client";
import { getKeyUnitStatusesQuery } from "../../util/query/keyRegister.query";

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

const S3_BASE_URL = "https://wfcrevampdoc.s3.us-east-1.amazonaws.com/";

const UNIT_STATUS_CONFIG = {
    live:     { label: "Live",     tagColor: "success" },
    replaced: { label: "Replaced", tagColor: "warning" },
    damaged:  { label: "Damaged",  tagColor: "error" },
};

const STATUS_CONFIG = {
    Live:      { color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f", tagColor: "success" },
    Available: { color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f", tagColor: "success" },
    Assigned:  { color: "#1677ff", bg: "#e6f4ff", border: "#91caff", tagColor: "processing" },
    Lost:      { color: "#ff4d4f", bg: "#fff2f0", border: "#ffccc7", tagColor: "error" },
    Damaged:   { color: "#fa8c16", bg: "#fff7e6", border: "#ffd591", tagColor: "warning" },
    Returned:  { color: "#722ed1", bg: "#f9f0ff", border: "#d3adf7", tagColor: "purple" },
};

const KEY_TYPE_CONFIG = {
    metal:      { label: "Metal",       icon: <KeyOutlined />,      color: "#8c8c8c" },
    fob:        { label: "Fob",         icon: <SafetyOutlined />,   color: "#1677ff" },
    card:       { label: "Card",        icon: <IdcardOutlined />,   color: "#722ed1" },
    accessCode: { label: "Access Code", icon: <BarcodeOutlined />,  color: "#13c2c2" },
};

const AUDIT_TAG_COLOR = {
    Scheduled: "processing",
    Completed: "success",
    Overdue:   "error",
    Pending:   "warning",
};

const getImageUrl = (image) => {
    if (!image) return null;
    if (typeof image === "string") return S3_BASE_URL + image;
    if (image.url) return image.url;
    return null;
};

const InfoRow = ({ label, value, icon, last }) => (
    <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "9px 0",
        borderBottom: last ? "none" : "1px solid #f5f5f5",
    }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {icon && <span style={{ color: "#8c8c8c", fontSize: 13 }}>{icon}</span>}
            <Text type="secondary" style={{ fontSize: 13 }}>{label}</Text>
        </div>
        <div>{value}</div>
    </div>
);

export const KeyOverview = ({ keyData }) => {
    const status = keyData?.status || "Live";
    const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.Live;
    const images = (keyData?.images || []).filter(Boolean);
    const [previewIndex, setPreviewIndex] = useState(null);

    const totalKeys = keyData?.totalKeys
        ?? (keyData?.keyTypes || []).reduce((s, kt) => s + (kt.count || 1), 0);

    const { data: unitStatusData } = useQuery(getKeyUnitStatusesQuery, {
        variables: { keyId: keyData?._id },
        skip: !keyData?._id,
        fetchPolicy: "network-only",
    });
    const unitStatuses = unitStatusData?.getKeyUnitStatuses?.response || [];
    const hasUnitHistory = unitStatuses.some(u => u.status !== "live");

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, overflowX:'hidden' }}>

            {/* Alert banners */}
            {keyData?.isKeyLost && (
                <Alert
                    type="error"
                    showIcon
                    message="This key is currently marked as Lost"
                    description={keyData?.lostNotes || undefined}
                    style={{ borderRadius: 8 }}
                />
            )}
            {keyData?.returnToCustomer && (
                <Alert
                    type="info"
                    showIcon
                    message="This key is marked for return to customer"
                    style={{ borderRadius: 8 }}
                />
            )}

            {/* ── Identity Card ── */}
            <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: "20px 24px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>

                    {/* Left: icon + serial + site */}
                    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                        <div style={{
                            width: 52, height: 52, borderRadius: 12,
                            background: statusConfig.bg,
                            border: `1.5px solid ${statusConfig.border}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 22, color: statusConfig.color, flexShrink: 0,
                        }}>
                            <KeyOutlined />
                        </div>
                        <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                                <Title level={5} style={{ margin: 0 }}>{keyData?.keySerial || "—"}</Title>
                                <Tag color={statusConfig.tagColor} style={{ borderRadius: 6, fontWeight: 500 }}>
                                    {status}
                                </Tag>
                            </div>
                            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <EnvironmentOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                        {keyData?.site?.sitename || "—"}
                                        {keyData?.site?.postcode ? ` · ${keyData.site.postcode}` : ""}
                                    </Text>
                                </div>
                                {keyData?.keygroup?.name && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <AppstoreOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
                                        <Text type="secondary" style={{ fontSize: 13 }}>{keyData.keygroup.name}</Text>
                                    </div>
                                )}
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <CalendarOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                        Received {keyData?.dateReceived ? dayjs(keyData.dateReceived).format("DD MMM YYYY") : "—"}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: custodian */}
                    <div style={{ textAlign: "right" }}>
                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 2 }}>Custodian Type</Text>
                        <Text strong style={{ fontSize: 14 }}>{keyData?.custodianType || "—"}</Text>
                        {keyData?.custodianDetail && (
                            <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                                <UserOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
                                <Text style={{ fontSize: 13 }}>{keyData.custodianDetail?.name || keyData.custodianDetail}</Text>
                            </div>
                        )}
                    </div>
                </div>

                {/* Key types + total */}
                <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    {(keyData?.keyTypes || []).map((kt, idx) => {
                        const cfg = KEY_TYPE_CONFIG[kt.type] || KEY_TYPE_CONFIG.metal;
                        return (
                            <div key={idx} style={{
                                display: "flex", alignItems: "center", gap: 6,
                                background: "#fafafa", border: "1px solid #f0f0f0",
                                borderRadius: 8, padding: "6px 12px",
                            }}>
                                <span style={{ color: cfg.color, fontSize: 14 }}>{cfg.icon}</span>
                                <Text style={{ fontSize: 13 }}>{cfg.label}</Text>
                                <Tag style={{ margin: 0, borderRadius: 4, fontSize: 12 }}>×{kt.count}</Tag>
                                {kt.accessCodes?.length > 0 && (
                                    <Tooltip title={kt.accessCodes.map(a => a.code).join(", ")}>
                                        <BarcodeOutlined style={{ color: "#13c2c2", fontSize: 12, cursor: "pointer" }} />
                                    </Tooltip>
                                )}
                            </div>
                        );
                    })}
                    <div style={{
                        display: "flex", alignItems: "center", gap: 6,
                        background: "#fafafa", border: "1px solid #f0f0f0",
                        borderRadius: 8, padding: "6px 12px",
                    }}>
                        <Text type="secondary" style={{ fontSize: 13 }}>Total:</Text>
                        <Text strong style={{ fontSize: 13 }}>{totalKeys}</Text>
                    </div>
                </div>

                {/* Per-unit status (only shown once a key has been replaced/damaged) */}
                {hasUnitHistory && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f5f5f5", display: "flex", flexDirection: "column", gap: 6 }}>
                        {(keyData?.keyTypes || []).map((kt) => {
                            const cfg = KEY_TYPE_CONFIG[kt.type] || KEY_TYPE_CONFIG.metal;
                            const units = unitStatuses.filter(u => u.keyType === kt.type);
                            if (!units.length) return null;
                            return (
                                <div key={kt.type} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                    <Text type="secondary" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                                        <span style={{ color: cfg.color }}>{cfg.icon}</span>{cfg.label}:
                                    </Text>
                                    {units.map(u => {
                                        const unitCfg = UNIT_STATUS_CONFIG[u.status] || UNIT_STATUS_CONFIG.live;
                                        return (
                                            <Tooltip
                                                key={u.index}
                                                title={u.lastEventDate ? `${cfg.label} #${u.index + 1} — ${unitCfg.label} on ${dayjs(u.lastEventDate).format("DD MMM YYYY")}` : `${cfg.label} #${u.index + 1} — Live`}
                                            >
                                                <Tag color={unitCfg.tagColor} style={{ margin: 0, borderRadius: 4, fontSize: 11 }}>
                                                    #{u.index + 1} {unitCfg.label}
                                                </Tag>
                                            </Tooltip>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>

            {/* ── Key Photos ── */}
            {images.length > 0 && (
                <Card
                    title={
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
                            <PictureOutlined />
                            <span>Key Photos ({images.length})</span>
                        </div>
                    }
                    style={{ borderRadius: 12 }}
                    bodyStyle={{ padding: "16px 24px" }}
                    headStyle={{ padding: "0 24px", minHeight: 44 }}
                >
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {images.map((img, idx) => (
                            <div
                                key={idx}
                                onClick={() => setPreviewIndex(idx)}
                                style={{
                                    width: 100, height: 100, borderRadius: 8, overflow: "hidden",
                                    border: "1px solid #f0f0f0", cursor: "pointer", flexShrink: 0,
                                    background: "#f5f5f5",
                                }}
                            >
                                <img
                                    src={getImageUrl(img)}
                                    alt={`key-photo-${idx}`}
                                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                    onError={e => { e.target.style.display = "none"; }}
                                />
                            </div>
                        ))}
                    </div>

                    <Modal
                        open={previewIndex !== null}
                        onCancel={() => setPreviewIndex(null)}
                        footer={null}
                        centered
                        width="auto"
                        styles={{ body: { padding: 0, textAlign: "center" }, content: { background: "transparent", boxShadow: "none" } }}
                    >
                        {previewIndex !== null && (
                            <div style={{ position: "relative", display: "inline-block" }}>
                                <img
                                    src={getImageUrl(images[previewIndex])}
                                    alt={`preview-${previewIndex}`}
                                    style={{ maxWidth: "80vw", maxHeight: "80vh", borderRadius: 8, display: "block" }}
                                />
                                {images.length > 1 && (
                                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, gap: 8 }}>
                                        <button
                                            onClick={() => setPreviewIndex((previewIndex - 1 + images.length) % images.length)}
                                            style={{ flex: 1, padding: "6px 16px", borderRadius: 6, border: "1px solid #d9d9d9", background: "#fff", cursor: "pointer", fontSize: 13 }}
                                        >
                                            ← Prev
                                        </button>
                                        <span style={{ lineHeight: "32px", fontSize: 13, color: "#fff" }}>
                                            {previewIndex + 1} / {images.length}
                                        </span>
                                        <button
                                            onClick={() => setPreviewIndex((previewIndex + 1) % images.length)}
                                            style={{ flex: 1, padding: "6px 16px", borderRadius: 6, border: "1px solid #d9d9d9", background: "#fff", cursor: "pointer", fontSize: 13 }}
                                        >
                                            Next →
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </Modal>
                </Card>
            )}

            {/* ── Details Grid ── */}
            <Row gutter={[12, 12]}>

                {/* Assignment & Custodian */}
                <Col xs={24} sm={12}>
                    <Card
                        title={
                            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
                                <TeamOutlined /><span>Assignment</span>
                            </div>
                        }
                        style={{ borderRadius: 12, height: "100%" }}
                        bodyStyle={{ padding: "4px 20px 12px" }}
                        headStyle={{ padding: "0 20px", minHeight: 44 }}
                    >
                        <InfoRow
                            label="Custodian Type"
                            value={<Text style={{ fontSize: 13 }}>{keyData?.custodianType || "—"}</Text>}
                            icon={<TeamOutlined />}
                        />
                        <InfoRow
                            label="Assignee"
                            value={<Text style={{ fontSize: 13 }}>{keyData?.custodianDetail?.name || keyData?.custodianDetail || "Unassigned"}</Text>}
                            icon={<IdcardOutlined />}
                        />
                        <InfoRow
                            label="Assignment Date"
                            value={
                                <Text style={{ fontSize: 13 }}>
                                    {keyData?.assignmentDate ? dayjs(keyData.assignmentDate).format("DD MMM YYYY") : "—"}
                                </Text>
                            }
                            icon={<CalendarOutlined />}
                        />
                        <InfoRow
                            label="Return to Customer"
                            value={
                                <Tag color={keyData?.returnToCustomer ? "warning" : "default"} style={{ margin: 0, borderRadius: 4 }}>
                                    {keyData?.returnToCustomer ? "Yes" : "No"}
                                </Tag>
                            }
                            icon={<LoginOutlined />}
                            last
                        />
                    </Card>
                </Col>

                {/* Audit Information */}
                <Col xs={24} sm={12}>
                    <Card
                        title={
                            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
                                <SafetyOutlined /><span>Audit</span>
                            </div>
                        }
                        style={{ borderRadius: 12, height: "100%" }}
                        bodyStyle={{ padding: "4px 20px 12px" }}
                        headStyle={{ padding: "0 20px", minHeight: 44 }}
                    >
                        <InfoRow
                            label="Audit Status"
                            value={
                                keyData?.auditStatus ? (
                                    <Tag
                                        color={AUDIT_TAG_COLOR[keyData.auditStatus] || "default"}
                                        style={{ margin: 0, borderRadius: 4 }}
                                    >
                                        {keyData.auditStatus}
                                    </Tag>
                                ) : <Text style={{ fontSize: 13, color: "#bfbfbf" }}>—</Text>
                            }
                            icon={<SafetyOutlined />}
                        />
                        <InfoRow
                            label="Next Audit"
                            value={
                                keyData?.auditNextDate ? (
                                    <Tooltip title={dayjs(keyData.auditNextDate).fromNow()}>
                                        <Text style={{
                                            fontSize: 13,
                                            color: dayjs(keyData.auditNextDate).isBefore(dayjs()) ? "#ff4d4f" : undefined,
                                        }}>
                                            {dayjs(keyData.auditNextDate).format("DD MMM YYYY")}
                                        </Text>
                                    </Tooltip>
                                ) : <Text style={{ fontSize: 13, color: "#bfbfbf" }}>—</Text>
                            }
                            icon={<ClockCircleOutlined />}
                        />
                        <InfoRow
                            label="Last Audit"
                            value={
                                <Text style={{ fontSize: 13 }}>
                                    {keyData?.auditLastDate ? dayjs(keyData.auditLastDate).format("DD MMM YYYY") : "—"}
                                </Text>
                            }
                            icon={<CheckCircleOutlined />}
                        />
                        <InfoRow
                            label="Key Lost"
                            value={
                                <Tag
                                    color={keyData?.isKeyLost ? "error" : "success"}
                                    style={{ margin: 0, borderRadius: 4 }}
                                >
                                    {keyData?.isKeyLost ? "Lost" : "Not Lost"}
                                </Tag>
                            }
                            icon={<WarningOutlined />}
                            last
                        />
                    </Card>
                </Col>
            </Row>

            {/* ── Lost / Return details (if relevant) ── */}
            {(keyData?.isKeyLost || keyData?.isReturned) && (
                <Row gutter={[12, 12]}>
                    {keyData?.isKeyLost && (
                        <Col xs={24} sm={keyData?.isReturned ? 12 : 24}>
                            <Card
                                title={
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#ff4d4f" }}>
                                        <WarningOutlined /><span>Lost Details</span>
                                    </div>
                                }
                                style={{ borderRadius: 12, borderColor: "#ffccc7" }}
                                bodyStyle={{ padding: "4px 20px 12px" }}
                                headStyle={{ padding: "0 20px", minHeight: 44 }}
                            >
                                <InfoRow
                                    label="Lost Date"
                                    value={
                                        <Text style={{ fontSize: 13 }}>
                                            {keyData?.lostDate ? dayjs(keyData.lostDate).format("DD MMM YYYY") : "—"}
                                        </Text>
                                    }
                                    icon={<CalendarOutlined />}
                                />
                                <InfoRow
                                    label="Reported By"
                                    value={<Text style={{ fontSize: 13 }}>{keyData?.lostReportedBy || "—"}</Text>}
                                    icon={<UserOutlined />}
                                />
                                <InfoRow
                                    label="Notes"
                                    value={<Text style={{ fontSize: 13 }}>{keyData?.lostNotes || "—"}</Text>}
                                    icon={<StopOutlined />}
                                    last
                                />
                            </Card>
                        </Col>
                    )}
                    {keyData?.isReturned && (
                        <Col xs={24} sm={keyData?.isKeyLost ? 12 : 24}>
                            <Card
                                title={
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#52c41a" }}>
                                        <CheckCircleOutlined /><span>Return Details</span>
                                    </div>
                                }
                                style={{ borderRadius: 12, borderColor: "#b7eb8f" }}
                                bodyStyle={{ padding: "4px 20px 12px" }}
                                headStyle={{ padding: "0 20px", minHeight: 44 }}
                            >
                                <InfoRow
                                    label="Return Date"
                                    value={
                                        <Text style={{ fontSize: 13 }}>
                                            {keyData?.returnDate ? dayjs(keyData.returnDate).format("DD MMM YYYY") : "—"}
                                        </Text>
                                    }
                                    icon={<CalendarOutlined />}
                                    last
                                />
                            </Card>
                        </Col>
                    )}
                </Row>
            )}
        </div>
    );
};
